import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RotateCcw, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2000;

const STATUS_STYLES = {
    completed: 'bg-emerald-50 text-emerald-700',
    completed_with_warnings: 'bg-amber-50 text-amber-700',
    running: 'bg-blue-50 text-blue-700',
    failed: 'bg-rose-50 text-rose-700',
    blocked_schema_drift: 'bg-amber-50 text-amber-700',
    reverted: 'bg-stone-100 text-stone-600',
    cancelled: 'bg-amber-50 text-amber-700',
};

const UNDOABLE_TRIGGERS = new Set(['manual_sync', 'test_import', 'multi_import']);
const UNDOABLE_STATUSES = new Set(['completed', 'cancelled', 'failed']);

function sumStats(stats) {
    if (!stats) return '—';
    const parts = [];
    for (const [entity, s] of Object.entries(stats)) {
        if (typeof s !== 'object') continue;
        const created = s.created || 0, updated = s.updated || 0;
        if (created || updated) parts.push(`${entity}: ${created}c/${updated}u`);
    }
    return parts.join(' · ') || 'no changes';
}

function ProgressCell({ run }) {
    const p = run.progress || {};
    if (!p.companies_total) return null;
    const pct = Math.round(((p.companies_done || 0) / p.companies_total) * 100);
    return (
        <span className="text-stone-500 text-[11px]">
            {p.companies_done || 0}/{p.companies_total} ({pct}%)
            {p.current && <> · {p.current}</>}
        </span>
    );
}

export default function RunHistory({ refreshKey, onChanged }) {
    const { getAuthHeader } = useAuth();
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [undoing, setUndoing] = useState(null);
    const [cancelling, setCancelling] = useState(null);
    const pollRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/pitchbook/runs`, { headers: getAuthHeader() });
            setRuns(res.data.runs || []);
        } catch { /* ignore */ } finally { setLoading(false); }
    }, [getAuthHeader]);

    useEffect(() => { load(); }, [load, refreshKey]);

    // Poll while any run is in-flight.
    useEffect(() => {
        const hasRunning = runs.some((r) => r.status === 'running');
        if (hasRunning && !pollRef.current) {
            pollRef.current = setInterval(async () => {
                try {
                    const res = await axios.get(`${API}/pitchbook/runs`, { headers: getAuthHeader() });
                    const updated = res.data.runs || [];
                    setRuns(updated);
                    if (!updated.some((r) => r.status === 'running')) {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        onChanged?.();
                    }
                } catch { /* ignore */ }
            }, POLL_INTERVAL_MS);
        }
        if (!hasRunning && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        return () => {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runs]);

    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    const undoRun = async (run) => {
        const count = run.operations?.length || 0;
        const label = run.status === 'cancelled' ? 'partially-completed' : run.status;
        const ok = window.confirm(
            `Undo this ${label} PitchBook import?\n\nThis will soft-delete records created and restore fields it updated when they have not changed since the sync.\n\nJournaled operations: ${count}`
        );
        if (!ok) return;
        setUndoing(run.run_id);
        try {
            const res = await axios.post(`${API}/pitchbook/runs/${run.run_id}/undo`, null, { headers: getAuthHeader() });
            const summary = res.data || {};
            toast.success(`Reverted: ${summary.soft_deleted || 0} records removed, ${summary.restored_fields || 0} fields restored`);
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to undo');
        } finally {
            setUndoing(null);
        }
    };

    const cancelRun = async (run) => {
        const ok = window.confirm(
            'Stop this import?\n\nRecords already written will be kept. You can use Undo afterward to revert the partial import.');
        if (!ok) return;
        setCancelling(run.run_id);
        try {
            await axios.post(`${API}/pitchbook/runs/${run.run_id}/cancel`, {}, { headers: getAuthHeader() });
            toast.success('Cancellation requested — the import will stop at the next checkpoint');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to cancel');
        } finally {
            setCancelling(null);
        }
    };

    if (loading) return <div className="py-6 text-center text-stone-400"><Loader2 className="mx-auto w-5 h-5 animate-spin" /></div>;
    if (runs.length === 0) return <p className="py-4 text-sm text-stone-400">No runs yet. Run a manual sync to see history here.</p>;

    return (
        <div className="overflow-x-auto rounded-lg border border-stone-200">
            <table className="w-full text-xs">
                <thead className="bg-stone-50 text-stone-500">
                    <tr>
                        <th className="px-3 py-2 font-medium text-left">Started</th>
                        <th className="px-3 py-2 font-medium text-left">Trigger</th>
                        <th className="px-3 py-2 font-medium text-left">File</th>
                        <th className="px-3 py-2 font-medium text-left">Results</th>
                        <th className="px-3 py-2 font-medium text-left">Status</th>
                        <th className="px-3 py-2 font-medium text-right">Action</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                    {runs.map((r) => {
                        const isRunning = r.status === 'running';
                        const canUndo = UNDOABLE_TRIGGERS.has(r.trigger) &&
                            UNDOABLE_STATUSES.has(r.status) &&
                            (r.operations?.length || 0) > 0 &&
                            r.status !== 'reverted';

                        return (
                            <React.Fragment key={r.run_id}>
                                <tr className="cursor-pointer hover:bg-stone-50" onClick={() => setSelected(selected === r.run_id ? null : r.run_id)}>
                                    <td className="px-3 py-2 text-stone-600">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</td>
                                    <td className="px-3 py-2 text-stone-600">{r.trigger}</td>
                                    <td className="px-3 py-2 text-stone-500 font-mono max-w-[200px] truncate">{(r.source_file_key || r.file_key || '').split('/').pop()}</td>
                                    <td className="px-3 py-2 text-stone-600">
                                        {isRunning ? <ProgressCell run={r} /> : sumStats(r.projection_stats)}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${STATUS_STYLES[r.status] || 'bg-stone-100 text-stone-600'}`}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <div className="inline-flex items-center gap-1.5">
                                            {isRunning && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); cancelRun(r); }}
                                                    disabled={cancelling === r.run_id}
                                                    className="inline-flex gap-1 items-center px-2 py-1 text-rose-600 rounded border border-rose-200 hover:bg-rose-50 disabled:opacity-50"
                                                >
                                                    {cancelling === r.run_id
                                                        ? <Loader2 className="w-3 h-3 animate-spin" />
                                                        : <XCircle className="w-3 h-3" />}
                                                    Stop
                                                </button>
                                            )}
                                            {canUndo && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); undoRun(r); }}
                                                    disabled={undoing === r.run_id}
                                                    className="inline-flex gap-1 items-center px-2 py-1 rounded border border-stone-200 text-stone-600 hover:bg-stone-100 disabled:opacity-50"
                                                >
                                                    {undoing === r.run_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                                                    Undo
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                {selected === r.run_id && (
                                    <tr className="bg-stone-50">
                                        <td colSpan={6} className="px-4 py-3">
                                            <pre className="text-[11px] text-stone-600 whitespace-pre-wrap">{JSON.stringify(r.projection_stats || {}, null, 2)}</pre>
                                            {r.progress && (
                                                <div className="text-[11px] text-stone-500 mt-2">
                                                    Progress: {r.progress.companies_done || 0}/{r.progress.companies_total || '?'} companies
                                                    {r.progress.current && ` · currently: ${r.progress.current}`}
                                                </div>
                                            )}
                                            {r.selection && (
                                                <div className="text-[11px] text-stone-500 mt-2">
                                                    Selection: {r.selection.include_company ? 'company included' : 'company skipped'} · {(r.selection.selected_person_ids || []).length} people selected
                                                </div>
                                            )}
                                            {r.selections?.length > 0 && (
                                                <div className="text-[11px] text-stone-500 mt-2">
                                                    Batch: {r.selections.length} compan{r.selections.length === 1 ? 'y' : 'ies'} · {r.selections.reduce((n, s) => n + (s.selected_person_ids || []).length, 0)} people selected
                                                </div>
                                            )}
                                            {r.undo_result && (
                                                <pre className="text-[11px] text-stone-600 whitespace-pre-wrap mt-2">{JSON.stringify(r.undo_result, null, 2)}</pre>
                                            )}
                                            {r.errors?.length > 0 && (
                                                <div className="mt-2 text-rose-600">{r.errors.map((e, i) => <div key={i}>• {e}</div>)}</div>
                                            )}
                                            {r.error && (
                                                <div className="text-rose-600 mt-2 text-[11px]">Error: {r.error}</div>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
